import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Package,
  Calendar, Download, RefreshCw, PieChart, LineChart,
  Star, Award, CreditCard, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

interface AnalyticsData {
  revenue: {
    total: number;
    monthly: number;
    growth: number;
    trend: 'up' | 'down';
  };
  orders: {
    total: number;
    monthly: number;
    growth: number;
    trend: 'up' | 'down';
    byStatus: { [key: string]: number };
  };
  customers: {
    total: number;
    new: number;
    returning: number;
    growth: number;
    trend: 'up' | 'down';
  };
  products: {
    total: number;
    active: number;
    lowStock: number;
    outOfStock: number;
  };
  topProducts: Array<{
    id: string;
    name: string;
    orders: number;
    revenue: number;
    growth: number;
  }>;
  recentActivity: Array<{
    id: string;
    type: 'order' | 'customer' | 'product';
    message: string;
    timestamp: string;
    value?: number;
  }>;
  monthlyData: Array<{
    month: string;
    revenue: number;
    orders: number;
    customers: number;
  }>;
}

export function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  // Mock analytics data based on our previous mock data
  const mockAnalyticsData: AnalyticsData = {
    revenue: {
      total: 14250.75,
      monthly: 4834.25,
      growth: 12.3,
      trend: 'up'
    },
    orders: {
      total: 47,
      monthly: 16,
      growth: 8.7,
      trend: 'up',
      byStatus: {
        pending: 3,
        confirmed: 5,
        production: 4,
        shipped: 2,
        delivered: 12,
        cancelled: 1
      }
    },
    customers: {
      total: 24,
      new: 6,
      returning: 18,
      growth: 15.2,
      trend: 'up'
    },
    products: {
      total: 4,
      active: 3,
      lowStock: 1,
      outOfStock: 0
    },
    topProducts: [
      {
        id: 'prod-001',
        name: 'Custom Karate Gi Tags',
        orders: 18,
        revenue: 612.00,
        growth: 22.5
      },
      {
        id: 'prod-002',
        name: 'Belt Rank Badges',
        orders: 12,
        revenue: 360.00,
        growth: 8.3
      },
      {
        id: 'prod-003',
        name: 'Dojo Name Patches',
        orders: 8,
        revenue: 240.00,
        growth: -3.2
      },
      {
        id: 'prod-004',
        name: 'Tournament Winner Tags',
        orders: 6,
        revenue: 330.00,
        growth: 45.8
      }
    ],
    recentActivity: [
      {
        id: 'act-001',
        type: 'order',
        message: 'New order #ORD-015 from Sarah Johnson',
        timestamp: '2024-10-14T09:30:00Z',
        value: 67.50
      },
      {
        id: 'act-002',
        type: 'customer',
        message: 'New customer Emma Davis registered',
        timestamp: '2024-10-13T16:45:00Z'
      },
      {
        id: 'act-003',
        type: 'order',
        message: 'Order #ORD-014 shipped to Mike Chen',
        timestamp: '2024-10-13T14:20:00Z',
        value: 123.75
      },
      {
        id: 'act-004',
        type: 'product',
        message: 'Custom Karate Gi Tags stock updated',
        timestamp: '2024-10-12T11:15:00Z'
      },
      {
        id: 'act-005',
        type: 'order',
        message: 'Order #ORD-013 delivered successfully',
        timestamp: '2024-10-12T08:30:00Z',
        value: 89.25
      }
    ],
    monthlyData: [
      { month: 'Apr', revenue: 3200, orders: 12, customers: 8 },
      { month: 'May', revenue: 3800, orders: 15, customers: 10 },
      { month: 'Jun', revenue: 4200, orders: 18, customers: 12 },
      { month: 'Jul', revenue: 3900, orders: 16, customers: 9 },
      { month: 'Aug', revenue: 4600, orders: 20, customers: 14 },
      { month: 'Sep', revenue: 5100, orders: 22, customers: 16 },
      { month: 'Oct', revenue: 4834, orders: 16, customers: 11 }
    ]
  };

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setData(mockAnalyticsData);
      setLoading(false);
    }, 800);
  }, [timeRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="mt-2 text-gray-600">Business insights and performance metrics</p>
        </div>
        <div className="flex space-x-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <Button variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${data.revenue.total.toLocaleString()}</p>
                <div className="flex items-center mt-1">
                  {data.revenue.trend === 'up' ? (
                    <ArrowUpRight className="h-4 w-4 text-green-600" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-600" />
                  )}
                  <span className={`text-sm ml-1 ${
                    data.revenue.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {data.revenue.growth}%
                  </span>
                  <span className="text-sm text-gray-500 ml-1">vs last period</span>
                </div>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{data.orders.total}</p>
                <div className="flex items-center mt-1">
                  {data.orders.trend === 'up' ? (
                    <ArrowUpRight className="h-4 w-4 text-green-600" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-600" />
                  )}
                  <span className={`text-sm ml-1 ${
                    data.orders.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {data.orders.growth}%
                  </span>
                  <span className="text-sm text-gray-500 ml-1">vs last period</span>
                </div>
              </div>
              <ShoppingCart className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{data.customers.total}</p>
                <div className="flex items-center mt-1">
                  {data.customers.trend === 'up' ? (
                    <ArrowUpRight className="h-4 w-4 text-green-600" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-600" />
                  )}
                  <span className={`text-sm ml-1 ${
                    data.customers.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {data.customers.growth}%
                  </span>
                  <span className="text-sm text-gray-500 ml-1">vs last period</span>
                </div>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Products</p>
                <p className="text-2xl font-bold text-gray-900">{data.products.active}</p>
                <div className="flex items-center mt-1">
                  <span className="text-sm text-gray-500">
                    {data.products.total} total, {data.products.lowStock} low stock
                  </span>
                </div>
              </div>
              <Package className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LineChart className="h-5 w-5" />
              Revenue Trend
            </CardTitle>
            <CardDescription>Monthly revenue over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-end justify-between space-x-2 p-4">
              {data.monthlyData.map((month) => (
                <div key={month.month} className="flex flex-col items-center flex-1">
                  <div 
                    className="bg-blue-500 rounded-t transition-all duration-300 w-full min-h-4"
                    style={{ 
                      height: `${(month.revenue / Math.max(...data.monthlyData.map(d => d.revenue))) * 200}px` 
                    }}
                  ></div>
                  <span className="text-xs text-gray-600 mt-2">{month.month}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Average monthly revenue: ${(data.monthlyData.reduce((sum, d) => sum + d.revenue, 0) / data.monthlyData.length).toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Order Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Order Status Distribution
            </CardTitle>
            <CardDescription>Current orders by status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.orders.byStatus).map(([status, count]) => {
                const total = Object.values(data.orders.byStatus).reduce((sum, c) => sum + c, 0);
                const percentage = total > 0 ? (count / total) * 100 : 0;
                const colors = {
                  pending: 'bg-yellow-500',
                  confirmed: 'bg-blue-500',
                  production: 'bg-purple-500',
                  shipped: 'bg-orange-500',
                  delivered: 'bg-green-500',
                  cancelled: 'bg-red-500'
                };
                
                return (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${colors[status as keyof typeof colors]}`}></div>
                      <span className="text-sm font-medium capitalize">{status}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${colors[status as keyof typeof colors]}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600 w-8">{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Top Performing Products
            </CardTitle>
            <CardDescription>Best selling products this period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.topProducts.map((product, index) => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                      <span className="text-sm font-bold text-blue-600">#{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.orders} orders</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">${product.revenue.toFixed(2)}</p>
                    <div className="flex items-center">
                      {product.growth > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      )}
                      <span className={`text-xs ml-1 ${
                        product.growth > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {product.growth > 0 ? '+' : ''}{product.growth.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Recent Activity
            </CardTitle>
            <CardDescription>Latest business activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.recentActivity.map((activity) => {
                const icons = {
                  order: ShoppingCart,
                  customer: Users,
                  product: Package
                };
                const colors = {
                  order: 'text-blue-600',
                  customer: 'text-green-600',
                  product: 'text-orange-600'
                };
                const Icon = icons[activity.type];
                
                return (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full bg-gray-100 ${colors[activity.type]}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{activity.message}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                        {activity.value && (
                          <span className="text-xs font-medium text-green-600">
                            +${activity.value.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Order Value</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${(data.revenue.total / data.orders.total).toFixed(2)}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Customer Retention</p>
                <p className="text-2xl font-bold text-gray-900">
                  {((data.customers.returning / data.customers.total) * 100).toFixed(1)}%
                </p>
              </div>
              <Award className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Growth</p>
                <p className="text-2xl font-bold text-gray-900">+{data.revenue.growth}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}